import { useCallback, useEffect, useState, } from "react";

type CameraPermissionStatus =
  | "idle"
  | "requesting"
  | "granted"
  | "denied"
  | "error";

export function useCameraPermission(requestOnMount = true,) {
  const [status, setStatus,] = useState<CameraPermissionStatus>("idle",);
  const [error, setError,] = useState<Error | null>(null,);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      setStatus("error",);
      setError(new Error("Нет доступа к window / navigator.",),);
      return false;
    }

    if (!window.isSecureContext) {
      setStatus("error",);
      setError(
        new Error(
          "Доступ к камере возможен только по HTTPS или на localhost.",
        ),
      );
      return false;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("error",);
      setError(new Error("Браузер не поддерживает getUserMedia.",),);
      return false;
    }

    setStatus("requesting",);
    setError(null,);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      },);

      // временный поток нам не нужен — сразу останавливаем
      stream.getTracks().forEach((track,) => {
        track.stop();
      },);

      setStatus("granted",);
      return true;
    } catch (err: any) {
      if (
        err?.name === "NotAllowedError"
        || err?.name === "PermissionDeniedError"
      ) {
        setStatus("denied",);
      } else {
        setStatus("error",);
      }

      setError(err instanceof Error ? err : new Error("Camera error",),);
      return false;
    }
  }, [],);

  useEffect(() => {
    if (requestOnMount) {
      void requestPermission();
    }
  }, [requestOnMount, requestPermission,],);

  return {
    status,
    error,
    isGranted: status === "granted",
    isRequesting: status === "requesting",
    requestPermission,
  };
}
